import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, X, FileUp, Sparkles, PenTool } from "lucide-react";
import { Button } from "@foundation/altzero_base/base/components/ui/button";
import { useUser } from "@foundation/altzero_base/base/contextapi/UserContext";
import { useToast } from "@foundation/altzero_base/base/hooks/use-toast";
import ScopingAILayout from "../components/ScopingAILayout";
import { DocumentPreview, UploadDialog } from "../components/documents/library";
import { TemplateCard } from "../components/documents/library/TemplateCard";
import AdvancedTemplateUploadDialog from "../components/templates/AdvancedTemplateUploadDialog";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  getAllTemplates,
  deleteCustomTemplate,
  duplicateTemplate,
  isEditorTemplate,
} from "../services/documentLibraryService";
import type { TemplateServiceConfig } from "../services/documentLibraryService";
import { ProcessedDocument, TemplateDocument } from "../types/document-library";

export type DocumentsLibraryConfig = {
  layout?: React.ComponentType<{ children: React.ReactNode }>;
  routes?: {
    back?: string;
    backLabel?: string;
    templateCreate?: string;
    templateEditorNew?: string;
    templateEditorById?: (id: string) => string;
    documentNew?: string;
  };
  api?: {
    uploadUrl?: string;
    apiKey?: string;
  };
  labels?: {
    title?: string;
    subtitle?: string;
  };
  serviceConfig?: TemplateServiceConfig;
};

export default function DocumentsLibrary({ config }: { config?: DocumentsLibraryConfig } = {}) {
  const { user } = useUser();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isTemplateSelection = searchParams.get("mode") === "select";

  const LayoutComponent = config?.layout ?? ScopingAILayout;
  const backRoute = config?.routes?.back ?? "/scopingai/resource-library";
  const backLabel = config?.routes?.backLabel ?? "Back to Resource Library";
  const templateCreateRoute = config?.routes?.templateCreate ?? "/scopingai/templates/create";
  const templateEditorNewRoute = config?.routes?.templateEditorNew ?? "/scopingai/proposal-template-editor/new";
  const getTemplateEditorRoute = config?.routes?.templateEditorById ?? ((id: string) => `/scopingai/proposal-template-editor/${id}`);
  const documentNewRoute = config?.routes?.documentNew ?? "/scopingai/documents/new";
  const uploadUrl = config?.api?.uploadUrl ?? "/api/scopingai/templates/upload";
  const apiKeyHeader = config?.api?.apiKey ?? "scopingai";
  const titleLabel = config?.labels?.title ?? "Templates";
  const subtitleLabel = config?.labels?.subtitle ?? "Manage and organize your proposal templates";
  const svcConfig = config?.serviceConfig;

  // Template selection callback
  const onTemplateSelect = (templateId: string) => {
    if (isTemplateSelection) {
      // Store the selected template in sessionStorage to preserve it
      sessionStorage.setItem("selectedTemplateFromLibrary", templateId);

      // Navigate back to the template selection step instead of creating new document
      navigate(documentNewRoute, {
        replace: true,
        state: {
          returnFromLibrary: true,
          selectedTemplate: templateId,
        },
      });
    } else {
      navigate(`${documentNewRoute}?template=${templateId}`);
    }
  };

  // State
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [processedDocument, setProcessedDocument] =
    useState<ProcessedDocument | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isTemplateUploading, setIsTemplateUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Template preview modal state
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] =
    useState<TemplateDocument | null>(null);

  // Advanced upload dialog state
  const [showAdvancedUpload, setShowAdvancedUpload] = useState(false);

  // Template documents state
  const [templateDocuments, setTemplateDocuments] = useState<
    TemplateDocument[]
  >([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [deletingTemplateId, setDeletingTemplateId] = useState<string | null>(null);

  // Load all templates on component mount with perfect consistency
  useEffect(() => {
    console.log('🔄 DocumentsLibrary: useEffect triggered', {
      hasUser: !!user,
      userId: user?.id,
      isLoadingTemplates,
      hasInitialized
    });

    if (user?.id && !hasInitialized) {
      console.log('🚀 User available, loading templates...');
      setHasInitialized(true);
      loadAllTemplates();
    } else if (user === null && !hasInitialized) {
      // User is explicitly null (not authenticated)
      console.log('❌ User not authenticated, stopping loading');
      setHasInitialized(true);
      setIsLoadingTemplates(false);
    }
    // If user is undefined, keep loading state true (still checking auth)
  }, [user, hasInitialized]);

  // 🚨 SAFETY: Timeout to prevent infinite loading
  useEffect(() => {
    const safetyTimeout = setTimeout(() => {
      if (isLoadingTemplates && !hasInitialized) {
        console.log('⚠️ Safety timeout: Stopping loading after 10 seconds');
        setIsLoadingTemplates(false);
        setHasInitialized(true);
        toast({
          title: "Loading Timeout",
          description: "Templates are taking longer than expected to load",
          variant: "destructive",
        });
      }
    }, 10000); // 10 second safety timeout

    return () => clearTimeout(safetyTimeout);
  }, [isLoadingTemplates, hasInitialized]);

  // Handle escape key to close template preview modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && showTemplatePreview) {
        setShowTemplatePreview(false);
        setPreviewTemplate(null);
      }
    };

    if (showTemplatePreview) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [showTemplatePreview]);

  // Load all templates with professional consistency
  const loadAllTemplates = async () => {
    try {
      setIsLoadingTemplates(true);

      // Only load templates if user is available
      if (!user?.id) {
        console.log("⏳ Waiting for user to load templates...");
        setIsLoadingTemplates(false);
        return;
      }

      console.log("🔄 Loading templates for user:", user.id);
      const allTemplates = await getAllTemplates(user.id, svcConfig);

      // Convert LibraryDocument to TemplateDocument format
      const convertedTemplates: TemplateDocument[] = allTemplates.map(
        (template) => ({
          id: String(template.id),
          title: template.title,
          description:
            template.rawData?.metadata?.description ||
            template.type ||
            "Template",
          date: template.date,
          sections: template.sections || [],
          rawData: template.rawData || {
            content: "",
            sections: [],
            metadata: {},
          },
        })
      );

      console.log("✅ Templates loaded successfully:", convertedTemplates.length);

      setTemplateDocuments(convertedTemplates);
    } catch (error) {
      console.error("❌ Error loading templates:", error);
      toast({
        title: "Error",
        description: "Failed to load templates",
        variant: "destructive",
      });
      setTemplateDocuments([]);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  // Reload templates without showing SuperLoader (for delete, duplicate, etc.)
  const reloadTemplatesQuietly = async () => {
    try {
      if (!user?.id) return;

      console.log("🔄 Quietly reloading templates...");
      const allTemplates = await getAllTemplates(user.id, svcConfig);

      const convertedTemplates: TemplateDocument[] = allTemplates.map(
        (template) => ({
          id: String(template.id),
          title: template.title,
          description:
            template.rawData?.metadata?.description ||
            template.type ||
            "Template",
          date: template.date,
          sections: template.sections || [],
          rawData: template.rawData || {
            content: "",
            sections: [],
            metadata: {},
          },
        })
      );

      setTemplateDocuments(convertedTemplates);
      console.log("✅ Templates reloaded quietly:", convertedTemplates.length);
    } catch (error) {
      console.error("❌ Error reloading templates:", error);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    try {
      setDeletingTemplateId(templateId);
      console.log('🗑️ Deleting template:', templateId);

      await deleteCustomTemplate(templateId, svcConfig);

      toast({
        title: "Success",
        description: "Template deleted successfully",
      });

      // Use quiet reload - no SuperLoader
      await reloadTemplatesQuietly();

      console.log('✅ Template deleted successfully');
    } catch (error) {
      console.error("❌ Error deleting template:", error);
      toast({
        title: "Error",
        description: "Failed to delete template",
        variant: "destructive",
      });
    } finally {
      setDeletingTemplateId(null);
    }
  };

  // Handle template preview
  const handleTemplatePreview = (templateId: string) => {
    const template = templateDocuments.find((t) => t.id === templateId);
    if (!template) return;

    const content = template.rawData?.content || "";
    const isEditor = isEditorTemplate(content) ||
      (template.rawData?.metadata as any)?.category === "Editor Template";

    if (isEditor) {
      // Editor templates → open in visual editor in preview mode
      navigate(`${getTemplateEditorRoute(template.id)}?preview=true`);
    } else {
      // Markdown templates → open modal preview
      setPreviewTemplate(template);
      setShowTemplatePreview(true);
    }
  };

  // Handle template edit (only for custom templates)
  const handleTemplateEdit = (templateId: string) => {
    const template = templateDocuments.find((t) => t.id === templateId);
    const isDefault =
      (template?.rawData?.metadata as any)?.isDefault || (template as any)?.isDefault;

    if (!template || isDefault) {
      toast({
        title: "Cannot Edit",
        description: "Default templates cannot be edited",
        variant: "destructive",
      });
      return;
    }

    // Route editor templates to the visual editor, markdown templates to the text editor
    const content = template.rawData?.content || "";
    const isEditor = isEditorTemplate(content) ||
      (template.rawData?.metadata as any)?.category === "Editor Template";
    if (isEditor) {
      navigate(getTemplateEditorRoute(template.id));
    } else {
      navigate(`${templateCreateRoute}?edit=true`, {
        state: {
          editTemplate: {
            id: template.id,
            title: template.title,
            description: template.description || "",
            content,
          },
        },
      });
    }
  };

  // Handle template duplication (for all templates)
  const handleTemplateDuplicate = async (templateId: string) => {
    if (!user?.id) {
      toast({
        title: "Error",
        description: "User not authenticated",
        variant: "destructive",
      });
      return;
    }

    try {
      const template = templateDocuments.find((t) => t.id === templateId);
      if (!template) {
        toast({
          title: "Template Not Found",
          description: "The selected template could not be found",
          variant: "destructive",
        });
        return;
      }

      // Create duplicate without "(Copy)" suffix
      await duplicateTemplate(
        templateId,
        {
          name: template.title,
          description: template.description,
          content: template.rawData?.content,
          sections: template.sections,
        },
        user.id,
        svcConfig
      );

      // Refresh templates quietly - no SuperLoader
      await reloadTemplatesQuietly();

      toast({
        title: "Success",
        description: "Template duplicated successfully",
      });
    } catch (error) {
      console.error("Error duplicating template:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to duplicate template",
        variant: "destructive",
      });
    }
  };

  // Handle when processed document is ready
  const handleProcessedDocument = (document: ProcessedDocument) => {
    setProcessedDocument(document);
    setShowPreview(true);
  };

  // Handle MD file import - Navigate to template creation page
  const handleMdImport = async (file: File) => {
    try {
      // Read the MD file content
      const content = await file.text();

      // Extract title from filename (remove .md extension)
      const title = file.name.replace(/\.md$/, "");

      // Navigate to template creation page with MD content
      navigate(`${templateCreateRoute}?import=true`, {
        state: {
          importedContent: content,
          importedTitle: title,
          importedFileName: file.name,
        },
      });

      toast({
        title: "Success",
        description: "MD file imported successfully",
      });
    } catch (error) {
      console.error("Error importing MD file:", error);
      toast({
        title: "Error",
        description: "Failed to import MD file",
        variant: "destructive",
      });
    }
  };

  // Handle template upload (PDF/DOCX) with progress and auto-open
  const handleTemplateUpload = async (file: File) => {
    if (!user?.id) {
      toast({
        title: "Error",
        description: "User not authenticated",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsTemplateUploading(true);
      setUploadProgress(0);
      // DON'T close dialog - keep it open to show progress

      // Show initial progress
      setUploadProgress(10);

      console.log('🚀 Starting template upload:', {
        fileName: file.name,
        fileSize: file.size,
        userId: user.id
      });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name.replace(/\.(pdf|docx)$/i, ''));
      formData.append('description', `Template imported from ${file.name}`);

      // Simulate progress during upload
      setUploadProgress(30);

      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'x-api-key': apiKeyHeader,
          'x-user-id': user.id,
        },
        body: formData,
      });

      setUploadProgress(70);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      const result = await response.json();
      setUploadProgress(90);

      console.log('✅ Template upload successful:', {
        templateId: result.template.id,
        templateName: result.template.name,
        sectionsCount: result.template.sections
      });

      toast({
        title: "Upload Complete!",
        description: `Template "${result.template.name}" uploaded successfully with ${result.template.sections} sections`,
      });

      setUploadProgress(100);

      // Reload templates quietly to show the new one - no SuperLoader
      await reloadTemplatesQuietly();

      // Close upload dialog after successful upload
      setShowAdvancedUpload(false);

      // 🚨 AUTO-OPEN: Navigate to edit the uploaded template
      setTimeout(() => {
        console.log('🔄 Auto-opening uploaded template for editing', {
          templateId: result.template.id,
          templateName: result.template.name,
          hasContent: !!result.template.content,
          contentPreview: result.template.content ? result.template.content.substring(0, 100) : 'no content'
        });

        navigate(`${templateCreateRoute}?edit=true`, {
          state: {
            editTemplate: {
              id: result.template.id,
              title: result.template.name,
              description: result.template.description,
              content: result.template.content,
              category: result.template.category || "general"
            }
          }
        });
      }, 1000); // Small delay to show completion

    } catch (error) {
      console.error("Error uploading template:", error);
      toast({
        title: "Upload Failed",
        description: error instanceof Error ? error.message : "Failed to upload template",
        variant: "destructive",
      });
    } finally {
      setTimeout(() => {
        setIsTemplateUploading(false);
        setUploadProgress(0);
      }, 1500);
    }
  };

  // Handle template saving for uploaded documents
  const handleSaveTemplate = async () => {
    if (!processedDocument || !showPreview) return;

    try {
      setIsUploading(true);

      // Navigate to template creation page with the processed content
      navigate(templateCreateRoute, {
        state: {
          importedContent: processedDocument.fullContent,
          importedTitle: processedDocument.title,
        },
      });

      toast({
        title: "Success",
        description: "Document processed successfully",
      });

      setShowPreview(false);
      setProcessedDocument(null);
      setIsUploadDialogOpen(false);
    } catch (error) {
      console.error("Error processing template:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to process template",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <LayoutComponent>
      <div>
        <div className="space-y-6">
          <div>
            {isTemplateSelection ? (
              <button
                onClick={() => navigate(documentNewRoute)}
                className="flex items-center text-muted-foreground hover:text-foreground mb-4"
              >
                <ArrowLeft size={16} className="mr-2" />
                Back to Template Selection
              </button>
            ) : backRoute ? (
              <Link
                to={backRoute}
                className="flex items-center text-muted-foreground hover:text-foreground mb-4"
              >
                <ArrowLeft size={16} className="mr-2" />
                {backLabel}
              </Link>
            ) : null}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold">
                  {isTemplateSelection ? "Select Template" : titleLabel}
                </h1>
                <p className="text-muted-foreground mt-1">
                  {isTemplateSelection
                    ? "Choose a template"
                    : subtitleLabel}
                </p>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => navigate(templateCreateRoute)}>
                  Create Template
                </Button>
              </div>
            </div>
          </div>

          {/* Templates Grid */}
          {isLoadingTemplates ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current mr-3" />
              <span className="text-sm">Loading templates...</span>
            </div>
          ) : templateDocuments.length === 0 ? (
            <div className="text-center py-12 border rounded-lg bg-muted/30">
              <p className="text-muted-foreground mb-4">No templates found</p>
              <div className="flex justify-center gap-2">
                {/* <Button
                  variant="outline"
                  onClick={() => setShowAdvancedUpload(true)}
                  disabled={isTemplateUploading}
                  className="gap-2"
                >
                  <Sparkles className="h-4 w-4" />
                  {isTemplateUploading ? "Processing..." : "Upload Sample Template"}
                </Button> */}
                <Button onClick={() => navigate(templateCreateRoute)}>
                  Create your first template
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {templateDocuments.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onPreview={handleTemplatePreview}
                  onEdit={handleTemplateEdit}
                  onDelete={handleDeleteTemplate}
                  onSelect={onTemplateSelect}
                  onDuplicate={handleTemplateDuplicate}
                  isSelectionMode={isTemplateSelection}
                  isDeleting={deletingTemplateId === template.id}
                />
              ))}
            </div>
          )}

          {/* Upload Dialog */}
          <UploadDialog
            isOpen={isUploadDialogOpen}
            onClose={() => setIsUploadDialogOpen(false)}
            onDocumentProcessed={handleProcessedDocument}
          />

          {/* Document Preview Dialog */}
          {showPreview && processedDocument && (
            <DocumentPreview
              processedDocument={processedDocument}
              onSave={handleSaveTemplate}
              onCancel={() => {
                setShowPreview(false);
                setProcessedDocument(null);
              }}
              isLoading={isUploading}
            />
          )}

          {/* Template Preview Modal */}
          {showTemplatePreview && previewTemplate && (
            <div
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setShowTemplatePreview(false);
                  setPreviewTemplate(null);
                }
              }}
            >
              <div className="bg-background rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
                  <div>
                    <h2 className="text-2xl font-bold text-foreground">
                      {previewTemplate.title}
                    </h2>
                    {previewTemplate.description && (
                      <p className="text-muted-foreground mt-1">
                        {previewTemplate.description}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowTemplatePreview(false);
                      setPreviewTemplate(null);
                    }}
                    className="h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {/* Modal Content */}
                <div className="flex-1 overflow-y-auto p-6">
                  <div className="prose prose-lg max-w-none dark:prose-invert prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-em:text-foreground prose-code:text-foreground prose-pre:text-foreground prose-blockquote:text-muted-foreground prose-li:text-foreground">
                    {previewTemplate.rawData?.content ? (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h1: ({ children }) => (
                            <h1 className="text-3xl font-bold mb-6 text-foreground border-b-2 border-primary pb-3 mt-8 first:mt-0">
                              {children}
                            </h1>
                          ),
                          h2: ({ children }) => (
                            <h2 className="text-2xl font-semibold mb-4 mt-8 text-foreground border-l-4 border-primary pl-4">
                              {children}
                            </h2>
                          ),
                          h3: ({ children }) => (
                            <h3 className="text-xl font-medium mb-3 mt-6 text-foreground">
                              {children}
                            </h3>
                          ),
                          h4: ({ children }) => (
                            <h4 className="text-lg font-medium mb-2 mt-4 text-foreground">
                              {children}
                            </h4>
                          ),
                          p: ({ children }) => (
                            <p className="mb-4 text-foreground leading-relaxed">
                              {children}
                            </p>
                          ),
                          ul: ({ children }) => (
                            <ul className="mb-4 ml-6 list-disc text-foreground">
                              {children}
                            </ul>
                          ),
                          ol: ({ children }) => (
                            <ol className="mb-4 ml-6 list-decimal text-foreground">
                              {children}
                            </ol>
                          ),
                          li: ({ children }) => (
                            <li className="mb-1 text-foreground">{children}</li>
                          ),
                          blockquote: ({ children }) => (
                            <blockquote className="border-l-4 border-muted pl-4 italic text-muted-foreground mb-4">
                              {children}
                            </blockquote>
                          ),
                          code: ({ children }) => (
                            <code className="bg-muted px-1 py-0.5 rounded text-sm font-mono text-foreground">
                              {children}
                            </code>
                          ),
                          pre: ({ children }) => (
                            <pre className="bg-muted p-4 rounded-lg overflow-x-auto mb-4">
                              {children}
                            </pre>
                          ),
                          img: ({ src, alt, ...props }) => (
                            <div className="my-6 text-center">
                              <img
                                src={src}
                                alt={alt || 'Document image'}
                                className="max-w-full h-auto mx-auto rounded-lg shadow-lg border border-border"
                                style={{ maxHeight: '600px' }}
                                onError={(e) => {
                                  console.error('Image failed to load:', src);
                                  e.currentTarget.style.display = 'none';
                                }}
                                {...props}
                              />
                              {alt && (
                                <div className="text-sm text-muted-foreground mt-2 italic">
                                  {alt}
                                </div>
                              )}
                            </div>
                          ),
                          hr: () => (
                            <div className="my-8 border-t-2 border-border opacity-50"></div>
                          ),
                        }}
                      >
                        {previewTemplate.rawData.content}
                      </ReactMarkdown>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>No content available for this template.</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="flex justify-end gap-2 p-6 border-t bg-muted/30 flex-shrink-0">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowTemplatePreview(false);
                      setPreviewTemplate(null);
                    }}
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Advanced Template Upload Dialog */}
        <AdvancedTemplateUploadDialog
          isOpen={showAdvancedUpload}
          onClose={() => setShowAdvancedUpload(false)}
          onWordUpload={handleTemplateUpload}
          onMarkdownUpload={handleMdImport}
          isUploading={isTemplateUploading}
          uploadProgress={uploadProgress}
        />
      </div>
    </LayoutComponent>
  );
}
